/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { isNotNil } from '@gh/core-util';
import { filter, isEmpty } from 'lodash';

const FORMAT_REGEXP = /(\[[^[]*\])|(\\)?([Hh]mm(ss)?|Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Qo?|YYYYYY|YYYYY|YYYY|YY|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|kk?|mm?|ss?|S{1,9}|x|X|zz?|ZZ?|.)/g;

const PM_TIME = 12;

const CURRENT_YEAR = new Date().getFullYear().toString().substring(0, 2);

type FormattedDate = {
  month?: number;
  year?: number;
  day?: number;
  hours?: number;
  minutes?: number;
};

const parses = {
  M: /\d\d?/,
  MM: /\d\d?/,
  D: /\d\d?/,
  DD: /\d\d?/,
  Y: /\d\d?/,
  YY: /\d\d?/,
  YYYY: /\d\d\d\d?/,
  HH: /\d\d?/,
  h: /\d\d?/,
  mm: /\d\d?/,
  A: /[ap]\.?m?\.?/i,
};

function getEmptyDate(): FormattedDate {
  return {
    month: void 0,
    year: void 0,
    day: void 0,
    hours: void 0,
    minutes: void 0,
  };
}

function getParseRegexp(param: string): string {
  return parses[param] || new RegExp(param);
}

const ADD_TO_DATE = {
  M: (formattedDate: FormattedDate, value: number) => {
    formattedDate.month = +value - 1;
  },
  MM: (formattedDate: FormattedDate, value: number) => {
    formattedDate.month = +value - 1;
  },
  D: (formattedDate: FormattedDate, value: number) => {
    formattedDate.day = +value;
  },
  DD: (formattedDate: FormattedDate, value: number) => {
    formattedDate.day = +value;
  },
  Y: (formattedDate: FormattedDate, value: number) => {
    formattedDate.year = +value;
  },
  YY: (formattedDate: FormattedDate, value: number) => {
    formattedDate.year = value >= 100 ? value : +(CURRENT_YEAR + value);
  },
  YYYY: (formattedDate: FormattedDate, value: number) => {
    formattedDate.year = +value;
  },
  h: (formattedDate: FormattedDate, value: number) => {
    formattedDate.hours = +value;
  },
  HH: (formattedDate: FormattedDate, value: number) => {
    formattedDate.hours = +value;
  },
  mm: (formattedDate: FormattedDate, value: number) => {
    formattedDate.minutes = +value;
  },
  A: (formattedDate: FormattedDate, value: string) => {
    if (value === 'PM') {
      formattedDate.hours! += PM_TIME;
    }
  },
};

function addToDate(formattedDate: FormattedDate, template: string, value: number | string): void {
  if (ADD_TO_DATE[template]) {
    ADD_TO_DATE[template](formattedDate, value);
  }
}

function isInvalidDate(formattedDate: FormattedDate): boolean {
  const MIN_YEAR = 1900;
  const MAX_MONTH = 11;
  const MAX_DAYS = 31;
  const MAX_HOURS = 24;
  const MAX_MINUTES = 60;

  if (isEmpty(filter(formattedDate, isNotNil))) {
    return true;
  }

  return (
    isNotNil(formattedDate.year) && (formattedDate.year < MIN_YEAR) ||
    isNotNil(formattedDate.month) && ((formattedDate.month < 0) || (formattedDate.month > MAX_MONTH)) ||
    isNotNil(formattedDate.day) && ((formattedDate.day < 1) || (formattedDate.day > MAX_DAYS)) ||
    isNotNil(formattedDate.hours) && ((formattedDate.hours < 0) || (formattedDate.hours > MAX_HOURS)) ||
    isNotNil(formattedDate.minutes) && ((formattedDate.minutes < 0) || (formattedDate.minutes > MAX_MINUTES)));
}

function newDateFromFormatted(formatted: FormattedDate): Date {
  return new Date(
    formatted.year || 1970,
    formatted.month || 0,
    formatted.day || 1,
    formatted.hours || 0,
    formatted.minutes || 0,
    0,
    0);
}

function getDate(formattedDate: FormattedDate): Date {
  return isInvalidDate(formattedDate) ? getInvalidDate() : newDateFromFormatted(formattedDate);
}

function getInvalidDate(): Date {
  return new Date('Invalid Date');
}

export function dateParse(input: string, format: string): Date {
  const formatArray = format.match(FORMAT_REGEXP);
  const formattedDate = getEmptyDate();

  let currentInput = input;

  if (!formatArray) {
    return getInvalidDate();
  }

  for (const currentFormat of formatArray) {
    if (currentFormat === 'YYYY' && +currentInput < 2000) {
      currentInput = CURRENT_YEAR + currentInput;
    }
    const regexp = currentInput.match(getParseRegexp(currentFormat));
    const value = regexp && regexp[0];

    if (value) {
      addToDate(formattedDate, currentFormat, value);
      currentInput = currentInput.slice(value.toString().length, currentInput.length);
    } else {
      return getInvalidDate();
    }
  }

  return currentInput.length > 0 ? getInvalidDate() : getDate(formattedDate);
}

export function isDateValid(input: string, format: string): boolean {
  return !isNaN(dateParse(input, format).getTime());
}
